/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Box, GlobalStyles, LinearProgress } from "@mui/material";
import { Controlled as CodeMirrorView } from "react-codemirror2";
// @ts-ignore
import "cm/mode/sql/sql";
// @ts-ignore
import "cm/addon/edit/matchbrackets";
// @ts-ignore
import "cm/addon/edit/closebrackets";
// @ts-ignore
import "cm/addon/scroll/simplescrollbars";
// @ts-ignore
import "cm/addon/comment/comment";
// @ts-ignore
import "cm/addon/comment/continuecomment";
// @ts-ignore
import "cm/addon/hint/show-hint";
// @ts-ignore
import "cm/addon/hint/sql-hint";
import { StriimMessageBox, StriimButton, StriimTypography } from "@striim/striim-ui";
import { StyledAiGradientTextButton } from "../../components/ai-gradient-button/ai-gradient-button.styles";
import App from "app";
import api from "core/api/api";
import {
    buildDryCompileTQL,
    dropDryCompileEntities,
    sanitizeDryCompileEntityError
} from "../../utils/dry-compile-tql";
import { Md5 } from "ts-md5";
import styles from "./tql-editor-tool-v3.styles";
import ConnectionValidationResults, { ConnectionValidationResult } from "./connection-validation-results";
import growl from "app/components/common/growl";
import { supportedConnectionMetas, validateConnections } from "./connection-validation";

const DONE_TQLs: any = {};

export interface TQLEditorToolV3Props {
    args: { tql?: string; isTransient?: boolean };
    respond?: (result: string) => void;
}

export const TQLEditorToolV3: React.FC<TQLEditorToolV3Props> = ({ args, respond }) => {
    const location = useLocation();
    const initialTql = args.tql ?? "";
    const initialMd5 = initialTql ? Md5.hashStr(initialTql) : "";
    const sensitiveMarksRef = useRef<any[]>([]);
    const revealedSensitiveKeysRef = useRef<Set<string>>(new Set());
    const acceptInFlightRef = useRef(false);

    const [code, setCode] = useState(initialTql);
    const [compiling, setCompiling] = useState(false);
    const [codeExecuted] = useState(DONE_TQLs[initialMd5] || false);
    const [messageText, setMessageText] = useState(DONE_TQLs[initialMd5] ? "Successfully executed your TQL" : "");
    const [messageType, setMessageType] = useState<"SUCCESS" | "INFO" | "ERROR" | "WARNING">(
        DONE_TQLs[initialMd5] ? "SUCCESS" : "INFO"
    );
    const [validationResults, setValidationResults] = useState<ConnectionValidationResult[] | null>(null);
    const [supportedAdapters, setSupportedAdapters] = useState<string[] | null>(null);

    useEffect(() => {
        setCode(args.tql ?? "");
    }, [args.tql]);

    // Fetch the adapters that have a backend wizard once, so we can skip
    // validating unsupported adapters and hide the control when none apply.
    useEffect(() => {
        let cancelled = false;
        // Wrapped in Promise.resolve().then() so a synchronous throw from the API layer
        // (e.g. the RMI transport not being initialized, as in Storybook) is caught below
        // instead of crashing the render tree.
        Promise.resolve()
            .then(() => (api as any).WizardHelper_GetAdaptersWithWizards())
            .then((list: string[]) => {
                if (!cancelled) setSupportedAdapters(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (!cancelled) setSupportedAdapters([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const hasSupportedConnection = useMemo(() => {
        if (!supportedAdapters || supportedAdapters.length === 0) return false;
        return supportedConnectionMetas(code, supportedAdapters).length > 0;
    }, [code, supportedAdapters]);

    const isDisabled = compiling || codeExecuted;

    const handleValidateConnections = async () => {
        setCompiling(true);
        setValidationResults(null);
        setMessageText("");
        try {
            const results = await validateConnections(code, supportedAdapters ?? []);
            if (results.length === 0) {
                setMessageText("No source or target connections found in TQL");
                setMessageType("WARNING");
            } else {
                setValidationResults(results);
            }
        } catch (e) {
            setMessageText(`Failed to validate connections: ${e?.message ?? e}`);
            setMessageType("ERROR");
        } finally {
            setCompiling(false);
        }
    };

    const handleDiscardTQL = () => {
        setMessageText("");
        setValidationResults(null);
        respond?.("User rejected the generated TQL and does not want to proceed with these changes.");
    };
    const handleAcceptTQL = async () => {
        if (acceptInFlightRef.current) return;
        acceptInFlightRef.current = true;

        const { tql: dryCompileTql, entities } = buildDryCompileTQL(code);
        try {
            setCompiling(true);
            setValidationResults(null);
            setMessageText("Validating TQL...");
            setMessageType("INFO");

            await dropDryCompileEntities(api, entities);
            try {
                await api.compileTQL(dryCompileTql);
            } finally {
                await dropDryCompileEntities(api, entities);
            }

            setMessageText("Successfully compiled your TQL");
            setMessageType("SUCCESS");

            const result = await api.compileTQL(code);

            const parts = location.pathname.split("/");
            const module = parts[1] ?? null;
            const currentApp = parts[2] ?? null;
            const md5 = Md5.hashStr(code);
            DONE_TQLs[md5] = true;

            setMessageText("Successfully executed your TQL");
            setMessageType("SUCCESS");

            const tqlAppMatch = code.match(/(?:ALTER|CREATE(?:\s+OR\s+REPLACE)?)\s+APPLICATION\s+([\w.]+)/i);
            let tqlApp = tqlAppMatch?.[1] ?? null;
            if (tqlApp && !tqlApp.includes(".")) {
                tqlApp = `${App.user.defaultNamespace}.${tqlApp}`;
            }

            if (module === "flow" && currentApp) {
                await api.compile(currentApp).catch((error: any) => {
                    growl.error(error.message, "TQL Compilation failed");
                });
            }

            const sameApp =
                module === "flow" && currentApp && tqlApp && currentApp.toLowerCase() === tqlApp.toLowerCase();

            if (args.isTransient) {
                respond?.("Component is created and This step is done. Please proceed to the next step of the skill.");
                return result;
            }

            App.vent.trigger("ai:reload-graph");

            if (sameApp) {
                respond?.("Say App is updated and provide the summary");
            } else {
                let navigated = false;
                if (tqlApp) {
                    try {
                        App.navigate("/flow/" + tqlApp + "?mode=conversational", { trigger: true });
                        navigated = true;
                    } catch (navError) {
                        console.error("Failed to navigate to Flow Designer", navError);
                    }
                }
                respond?.(
                    navigated
                        ? `Navigated to the Flow Designer for ${tqlApp}. Say App is updated and provide the summary.`
                        : "Say the app was updated and provide the summary."
                );
                return result;
            }
        } catch (e) {
            console.error(e);
            const rawMessage = e instanceof Error ? e.message : String(e);
            setMessageText("Error compiling your TQL: " + sanitizeDryCompileEntityError(rawMessage, entities));
            setMessageType("ERROR");
        } finally {
            setCompiling(false);
            acceptInFlightRef.current = false;
        }
    };

    return (
        <Box sx={styles.root}>
            <GlobalStyles
                styles={{
                    "@keyframes tql-v3-highlight-fade": {
                        "0%": { background: "rgba(99,102,241,0.15)" },
                        "60%": { background: "rgba(99,102,241,0.15)" },
                        "100%": { background: "transparent" }
                    },
                    ".tql-v3-line-highlight": {
                        animation: "tql-v3-highlight-fade 2.5s ease-out forwards"
                    }
                }}
            />

            {/* Header: title */}
            <Box sx={styles.header}>
                <Box sx={styles.titleGroup}>
                    <StriimTypography variant="h3">Review Generated TQL</StriimTypography>
                    <StriimTypography variant="body4" sx={styles.disclaimer}>
                        AI can make mistakes, please verify before executing.
                    </StriimTypography>
                </Box>
            </Box>

            {/* Full-width editor */}
            <Box
                sx={{
                    ...styles.editorWrapper,
                    opacity: isDisabled ? 0.7 : 1,
                    transition: "opacity 0.3s"
                }}
            >
                <CodeMirrorView
                    value={code}
                    onBeforeChange={(_editor, _data, value) => {
                        if (!isDisabled) setCode(value);
                    }}
                    options={{
                        mode: "text/x-tql",
                        lineNumbers: true,
                        gutters: ["CodeMirror-linenumbers"],
                        matchBrackets: true,
                        autoCloseBrackets: true,
                        readOnly: isDisabled ? "nocursor" : false,
                        extraKeys: { "Ctrl-/": "toggleComment" }
                    }}
                />
            </Box>

            <LinearProgress hidden={!compiling} style={{ width: "99%", margin: "0 auto" }} />

            {validationResults && (
                <Box sx={{ mt: 0.5 }}>
                    <ConnectionValidationResults results={validationResults} />
                </Box>
            )}
            {messageText && (
                <Box sx={{ mt: 0.5 }}>
                    <StriimMessageBox
                        type={messageType}
                        text={messageText}
                        customCardStyles={{ maxHeight: 250, overflow: "auto" }}
                    />
                </Box>
            )}

            <Box sx={styles.buttonsRow}>
                <StriimButton variant="secondary" size="small" disabled={isDisabled} onClick={handleDiscardTQL}>
                    Discard
                </StriimButton>
                <Box sx={styles.buttonsRowRight}>
                    {hasSupportedConnection && (
                        <StriimButton
                            variant="secondary"
                            size="small"
                            disabled={isDisabled}
                            onClick={handleValidateConnections}
                        >
                            Validate Connections
                        </StriimButton>
                    )}
                    <StyledAiGradientTextButton size="small" disabled={isDisabled} onClick={handleAcceptTQL}>
                        Accept TQL
                    </StyledAiGradientTextButton>
                </Box>
            </Box>
        </Box>
    );
};
